import type { DirectionalIndicatorEdge } from '../core';

export type CollectionReorderPointerPreference = 'vertical' | 'horizontal' | 'auto';

export type CollectionReorderRendererKind = 'grid' | 'carousel' | 'alternating' | 'accordion';

/**
 * Chooses how pointer position maps to “before vs after” for collection reorder:
 * - **Grid:** left/right edges match column gaps.
 * - **Alternating / single-column accordion:** top/bottom match vertical stack gaps.
 * - **Multi-column accordion:** `auto` so both axes work between grid-placed cards.
 * - **Carousel:** follows carousel orientation (horizontal strip vs vertical stack).
 */
export function resolveCollectionReorderPointerPreference(input: {
  renderer: CollectionReorderRendererKind;
  carouselOrientation?: 'horizontal' | 'vertical';
  accordionMultiColumn?: boolean;
}): CollectionReorderPointerPreference {
  const { renderer, carouselOrientation, accordionMultiColumn } = input;
  if (renderer === 'grid') {
    return 'horizontal';
  }
  if (renderer === 'alternating') {
    return 'vertical';
  }
  if (renderer === 'accordion') {
    return accordionMultiColumn ? 'auto' : 'vertical';
  }
  if (renderer === 'carousel') {
    return carouselOrientation === 'vertical' ? 'vertical' : 'horizontal';
  }
  return 'auto';
}

export function resolvePointerEdgeWithinRect(
  clientX: number,
  clientY: number,
  rect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>,
  preference: CollectionReorderPointerPreference,
): DirectionalIndicatorEdge {
  const midX = rect.left + rect.width / 2;
  const midY = rect.top + rect.height / 2;
  const dx = clientX - midX;
  const dy = clientY - midY;

  if (preference === 'horizontal') {
    return dx < 0 ? 'left' : 'right';
  }
  if (preference === 'vertical') {
    return dy < 0 ? 'top' : 'bottom';
  }
  /**
   * `auto` picks left/right vs top/bottom from pointer deltas. On **wide, short** rows (alternating
   * cards, accordion rows), |dx| almost always wins → **left/right** edges. Placement chrome uses
   * slim side strips (`w-8`), so the “gap” looks missing; top/bottom use full-width bars.
   * Prefer vertical split whenever the rect reads like a horizontal strip.
   */
  if (preference === 'auto') {
    const rectWidth = rect.width;
    const rectHeight = rect.height;
    if (rectHeight > 0 && rectWidth > rectHeight * 1.25) {
      return clientY < midY ? 'top' : 'bottom';
    }
  }
  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx < 0 ? 'left' : 'right';
  }
  return dy < 0 ? 'top' : 'bottom';
}

export function readClientPointFromDragNativeEvent(nativeEvent: Event | undefined): {
  x: number;
  y: number;
} | null {
  if (!nativeEvent) {
    return null;
  }
  if ('clientX' in nativeEvent && typeof (nativeEvent as PointerEvent).clientX === 'number') {
    const pe = nativeEvent as PointerEvent;
    return { x: pe.clientX, y: pe.clientY };
  }
  if ('touches' in nativeEvent) {
    const touch =
      (nativeEvent as TouchEvent).touches[0] ?? (nativeEvent as TouchEvent).changedTouches[0];
    if (touch) {
      return { x: touch.clientX, y: touch.clientY };
    }
  }
  return null;
}
